import { availableAuditEntriesAtom } from '@/global-store';
import fetchWrapper from '@/http/fetchWrapper';
import { api } from '@http/constants';
import type { AuditData } from '@eye-able/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

export interface AuditEntryPinVariables {
  auditEntryId: number;
}

/** Pin a domain for the current user (`POST /auditEntryPins`). */
export function usePinAuditEntry() {
  return useAuditEntryPinMutation(true);
}

/** Unpin a domain for the current user (`DELETE /auditEntryPins`). */
export function useUnpinAuditEntry() {
  return useAuditEntryPinMutation(false);
}

function useAuditEntryPinMutation(isPinned: boolean) {
  const queryClient = useQueryClient();
  const setAvailableAuditEntries = useSetAtom(availableAuditEntriesAtom);

  return useMutation({
    mutationKey: [api.auditEntryPins],
    mutationFn: ({ auditEntryId }: AuditEntryPinVariables) =>
      isPinned
        ? fetchWrapper.post<void>(api.auditEntryPins, { auditEntryId })
        : fetchWrapper.delete<void>(api.auditEntryPins, { auditEntryId }),
    onSuccess: async (_data, { auditEntryId }) => {
      setAvailableAuditEntries((entries) =>
        reorderPinnedAuditEntries(entries, auditEntryId, isPinned),
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [api.listAllLicenses] }),
        queryClient.invalidateQueries({ queryKey: [api.homeDomains] }),
      ]);
    },
  });
}

/**
 * Keeps pinned entries first: a new pin goes to the top, an unpinned entry
 * moves to the head of the unpinned rest.
 */
function reorderPinnedAuditEntries(
  entries: AuditData[],
  auditEntryId: number,
  isPinned: boolean,
): AuditData[] {
  const target = entries.find((entry) => entry.id === auditEntryId);
  if (!target) return entries;

  const updatedEntry = { ...target, isPinned };
  const rest = entries.filter((entry) => entry.id !== auditEntryId);
  if (isPinned) return [updatedEntry, ...rest];

  const pinned = rest.filter((entry) => entry.isPinned);
  const unpinned = rest.filter((entry) => !entry.isPinned);
  return [...pinned, updatedEntry, ...unpinned];
}
